import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { STORAGE_KEYS } from '../../modules/auth/constants';
import { Clock, MapPin, Plus, Edit2, Trash2, X, Check, AlertCircle } from 'lucide-react';
import SuccessToast from '../../components/SuccessToast';

const API_BASE = '/api/admin/working-hours';
const LOCATIONS_API = '/api/v2/organization-locations/active';

export default function AdminWorkingHours() {
  const [policies, setPolicies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    country: '',
    location: '',
    work_hours: 8.0,
    break_minutes: 60,
    overtime_start_after: '',
    is_default: false
  });

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await axios.get(API_BASE, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPolicies(response.data.data || []);
      } else {
        setError(response.data.error || 'Failed to load working hours policies');
      }
    } catch (err) {
      console.error('Load policies error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load working hours policies');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await axios.get(LOCATIONS_API, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setLocations(response.data.data || []);
      }
    } catch (err) {
      console.error('Load locations error:', err);
    }
  };

  const handleCreate = () => {
    setEditingPolicy(null);
    setFormData({
      country: '',
      location: '',
      work_hours: 8.0,
      break_minutes: 60,
      overtime_start_after: '',
      is_default: false
    });
    setShowModal(true);
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      country: policy.country || '',
      location: policy.location || '',
      work_hours: policy.work_hours || 8.0,
      break_minutes: policy.break_minutes ?? 60,
      overtime_start_after: policy.overtime_start_after || '',
      is_default: !!policy.is_default
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this working hours policy?')) return;
    try {
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const response = await axios.delete(`${API_BASE}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setSuccessMsg('Working hours policy deleted successfully.');
        loadPolicies();
      } else {
        setError(response.data.error || 'Failed to delete policy');
      }
    } catch (err) {
      console.error('Delete policy error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete policy');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN);
      const payload = {
        ...formData,
        work_hours: parseFloat(formData.work_hours),
        break_minutes: parseInt(formData.break_minutes, 10),
        overtime_start_after: formData.overtime_start_after === '' || formData.overtime_start_after === null
          ? null
          : parseFloat(formData.overtime_start_after),
        is_default: !!formData.is_default
      };

      let response;
      if (editingPolicy) {
        response = await axios.put(`${API_BASE}/${editingPolicy.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(API_BASE, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.data.success) {
        setShowModal(false);
        setSuccessMsg(editingPolicy ? 'Working hours policy updated successfully.' : 'Working hours policy created successfully.');
        loadPolicies();
      } else {
        setError(response.data.error || 'Failed to save policy');
      }
    } catch (err) {
      console.error('Save policy error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save policy');
    }
  };

  const handleLocationChange = (e) => {
    const selectedCity = e.target.value;
    setFormData(f => ({ ...f, location: selectedCity }));
  };

  useEffect(() => {
    loadPolicies();
    loadLocations();
  }, []);

  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
    fontSize: 14,
    color: '#1E293B',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#FFFFFF'
  };

  const labelStyle = {
    fontSize: 12,
    fontWeight: 600,
    color: '#374151',
    display: 'block',
    marginBottom: 6
  };

  return (
    <div style={{ fontFamily: "'Inter', ui-sans-serif, system-ui, sans-serif", padding: 24, background: '#F1F5F9', minHeight: '100vh' }}>
      <SuccessToast message={successMsg} onClose={() => setSuccessMsg('')} />
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Clock size={28} color="#0F172A" />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1E293B', margin: 0 }}>Working Hours</h1>
          </div>
          <button
            onClick={handleCreate}
            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
          >
            <Plus size={18} />
            Add Policy
          </button>
        </div>

        {error && (
          <div style={{ padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 24 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>Loading working hours policies...</div>
        ) : policies.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#64748B', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12 }}>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: '#1E293B' }}>No working hours policies found</div>
            <div style={{ fontSize: 13 }}>Click "Add Policy" to create your first working hours policy.</div>
          </div>
        ) : (
          <div style={{ background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 12, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Location</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Country</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Work Hours</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Break</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Overtime After</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Default</th>
                  <th style={{ textAlign: 'left', padding: 12, color: '#64748B', fontWeight: 600 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((policy) => (
                  <tr key={policy.id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                    <td style={{ padding: 12, color: '#1E293B', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <MapPin size={14} color="#64748B" />
                        {policy.location || '—'}
                      </div>
                    </td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{policy.country || '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{policy.work_hours ?? '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{policy.break_minutes ?? '—'} min</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>{policy.overtime_start_after ?? '—'}</td>
                    <td style={{ padding: 12, color: '#1E293B' }}>
                      {policy.is_default ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#DCFCE7', color: '#166534', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                          <Check size={12} /> Default
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td style={{ padding: 12 }}>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => handleEdit(policy)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#F1F5F9', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                        >
                          <Edit2 size={14} /> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(policy.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: '#FEF2F2', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#EF4444', cursor: 'pointer' }}
                        >
                          <Trash2 size={14} /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
          <div style={{ background: '#FFFFFF', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 24px 48px rgba(15,23,42,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #E2E8F0' }}>
              <h2 style={{ fontSize: 17, fontWeight: 700, color: '#1E293B', margin: 0 }}>
                {editingPolicy ? 'Edit Working Hours Policy' : 'Add Working Hours Policy'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#64748B', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: '22px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={labelStyle}>Country</label>
                  <input
                    type="text"
                    placeholder="e.g., India"
                    value={formData.country}
                    onChange={(e) => setFormData(f => ({ ...f, country: e.target.value }))}
                    style={inputStyle}
                  />
                </div>

                <div>
                  <label style={labelStyle}>Location *</label>
                  <select
                    value={formData.location}
                    onChange={handleLocationChange}
                    required
                    style={inputStyle}
                  >
                    <option value="">Select a location</option>
                    {locations.map((loc) => (
                      <option key={loc.location_id} value={loc.city}>
                        {loc.city}{loc.state ? `, ${loc.state}` : ''}
                      </option>
                    ))}
                    {formData.location && !locations.some(l => l.city === formData.location) && (
                      <option value={formData.location}>{formData.location} (not in location master)</option>
                    )}
                  </select>
                  <p style={{ fontSize: 12, color: '#64748B', margin: '6px 0 0' }}>
                    Locations are loaded from <strong>Location Master</strong>. Selecting a location saves its city name.
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Work Hours *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="24"
                      required
                      value={formData.work_hours}
                      onChange={(e) => setFormData(f => ({ ...f, work_hours: e.target.value }))}
                      style={inputStyle}
                    />
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '4px 0 0' }}>Total hours per day, e.g. 8.5</p>
                  </div>
                  <div>
                    <label style={labelStyle}>Break Minutes *</label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={formData.break_minutes}
                      onChange={(e) => setFormData(f => ({ ...f, break_minutes: e.target.value }))}
                      style={inputStyle}
                    />
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '4px 0 0' }}>e.g. 60</p>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={labelStyle}>Overtime Start After</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max="24"
                      value={formData.overtime_start_after}
                      onChange={(e) => setFormData(f => ({ ...f, overtime_start_after: e.target.value }))}
                      style={inputStyle}
                    />
                    <p style={{ fontSize: 11, color: '#94A3B8', margin: '4px 0 0' }}>Optional</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, paddingBottom: 8 }}>
                    <input
                      id="is_default"
                      type="checkbox"
                      checked={formData.is_default}
                      onChange={(e) => setFormData(f => ({ ...f, is_default: e.target.checked }))}
                      style={{ width: 18, height: 18, cursor: 'pointer' }}
                    />
                    <label htmlFor="is_default" style={{ fontSize: 14, color: '#1E293B', cursor: 'pointer', fontWeight: 500 }}>
                      Set as default policy
                    </label>
                  </div>
                </div>

                {locations.length === 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: '#FFFBEB', border: '1px solid #FEF3C7', borderRadius: 8, color: '#92400E', fontSize: 13 }}>
                    <AlertCircle size={16} />
                    No locations found in Location Master. Please add locations first.
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{ padding: '10px 18px', background: '#FFFFFF', border: '1px solid #E2E8F0', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#374151', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{ padding: '10px 18px', background: '#0F172A', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, color: '#FFFFFF', cursor: 'pointer' }}
                >
                  {editingPolicy ? 'Update Policy' : 'Create Policy'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
